
import React, { useEffect, useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll(".fade-in-element");
            elements.forEach((el, index) => {
              setTimeout(() => {
                el.classList.add("animate-fade-in");
              }, index * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const faqs = [
    {
      question: "Who can participate in ai4hack?",
      answer: "AI4Hack is designed for experienced developers, engineers, data scientists, product managers, and technical founders working with AI. We welcome professionals who are actively building or researching solutions in Fintech, Healthcare, Generative AI, and AI Agents."
    },
    {
      question: "What do I need to bring to the hackathon?",
      answer: "Since all events are fully remote, you just need your computer, a stable internet connection, and your passion for AI."
    },
    {
      question: "How does the $25K funding work?",
      answer: "Top teams may receive an investment offer of up to $25,000, depending on the evaluation by our partner fund. Selection is based on criteria such as team strength, market potential, and project validation. Please note, that decision is made independently by the fund and is not guaranteed."
    },
    {
      question: "How does the acceleration happen?",
      answer: "Promising teams may be invited to a custom acceleration program with our partners — focused on mentorship, product development, and go-to-market support. Participation is not automatic and depends on partner selection."
    },
    {
      question: "Can I participate remotely?",
      answer: "Yes! All hackathon events are fully remote and accessible online. While we promote the event regionally to build local communities, participants from anywhere in the world can join and compete in any track that interests them."
    },
    {
      question: "What if I want to participate alone?",
      answer: "You can, just fill in the data, and select you have a team, but don't fill in any other info, we will know that you are a solo-founder. Same thing for teams less than 4 people."
    }
  ];

  return (
    <section id="faq" style={{ backgroundColor: '#F2F3EE' }} className="w-full m-0 p-0 py-12 sm:py-16 md:py-20 mb-[-120px] sm:mb-[-160px] md:mb-[-200px] relative z-20 overflow-visible" ref={sectionRef}>
      <div className="container px-4 sm:px-6 lg:px-8 mx-auto" style={{ backgroundColor: '#F2F3EE' }}>
        <div className="flex justify-center mb-8 sm:mb-16">
          <div className="inline-flex items-center justify-center px-4 py-2 rounded-full opacity-0 fade-in-element" style={{
            backgroundColor: 'rgba(242, 76, 39, 0.1)',
            border: '1px solid rgba(242, 76, 39, 0.2)',
            color: 'rgba(242, 76, 39, 1)'
          }}>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white mr-2" style={{
              backgroundColor: 'rgba(242, 76, 39, 1)'
            }}>7</span>
            <span>FAQ</span>
          </div>
        </div>
        
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mb-6 pb-2 opacity-0 fade-in-element" style={{
            background: 'linear-gradient(135deg, rgba(242, 76, 39, 1) 0%, rgba(31, 31, 30, 1) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}>
            <span className="hidden md:inline">Frequently Asked Questions</span>
            <span className="md:hidden">Frequent Questions</span>
          </h2>
          <p className="text-lg opacity-0 fade-in-element" style={{
            color: 'rgba(77, 77, 77, 1)'
          }}>
            Everything you need to know about joining ai4hack
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="group border-none">
                <div
                  className="rounded-2xl overflow-hidden opacity-0 fade-in-element bg-white/70 backdrop-blur border border-white/20 shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 group-data-[state=open]:bg-[rgba(242,76,39,0.08)] group-data-[state=open]:border-[rgba(242,76,39,0.2)] group-data-[state=open]:shadow-[0_6px_24px_rgba(242,76,39,0.15)]"
                  style={{
                    animationDelay: `${0.1 * index}s`
                  }}
                >
                  <AccordionTrigger
                    className="px-6 py-6 text-left hover:no-underline transition-all duration-300 text-[rgba(57,55,55,1)] group-data-[state=open]:text-[rgba(242,76,39,1)]"
                  >
                    <span className="text-lg font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <p className="text-base leading-relaxed" style={{
                      color: 'rgba(77, 77, 77, 1)'
                    }}>
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </div>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
